import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import StockTradingPage from '@/features/trading-history/components/StockTradingPage'
import {
  StockTradingBodySkeleton,
  StockTradingToolbarSkeleton,
} from '@/features/trading-history/components/StockTradingSkeleton'
import { toTradingSearch, tradingSearchSchema } from '@/features/trading-history/lib/tradingSearch'
import { loadStockTrading } from '@/screens/market/StockTradingScreen.loader'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Stock Trading" />

function StockTradingScreenPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading stock trading…"
      header={HEADER}
      pageTitle="Stock Trading"
    >
      <div className="flex flex-col gap-4">
        <StockTradingToolbarSkeleton />
        <StockTradingBodySkeleton />
      </div>
    </ScreenLayout>
  )
}

function StockTradingScreenError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load stock trading" />
}

export const Route = createFileRoute('/_app/market/stock-trading')({
  validateSearch: tradingSearchSchema,
  loader: loadStockTrading,
  pendingComponent: StockTradingScreenPending,
  errorComponent: StockTradingScreenError,
  component: StockTradingScreen,
})

function StockTradingScreen() {
  // Re-parsed, not read straight off `Route.useSearch()`: the router merges the
  // raw query string into it, so a stray param would key the read off a
  // different object than the one the loader warmed.
  const search = toTradingSearch(Route.useSearch())
  const navigate = Route.useNavigate()

  return (
    <ScreenLayout header={HEADER} pageTitle="Stock Trading">
      <StockTradingPage
        search={search}
        onSearchChange={(patch) => {
          void navigate({ search: (prev) => ({ ...prev, ...patch }) })
        }}
      />
    </ScreenLayout>
  )
}
